import { FormEvent, useEffect, useState } from 'react';
import {
  CircleUserRound,
  KeyRound,
  Loader2,
  Save,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { profileApi } from '../../../api/profileApi';
import { getApiErrorMessage } from '../../../api/apiClient';
import { UserProfile } from '../../../types/profile';

export function ProfileSettings() {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await profileApi.getProfile();

        setProfile(data);

        setForm({
          firstName: data.firstName ?? '',
          lastName: data.lastName ?? '',
          email: data.email ?? '',
          phoneNumber: data.phoneNumber ?? '',
        });
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            'Unable to load your profile.'
          )
        );
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleProfileSubmit = async (
    event: FormEvent
  ) => {
    event.preventDefault();

    if (!form.firstName.trim()) {
      toast.error('First name is required.');
      return;
    }

    if (!form.lastName.trim()) {
      toast.error('Last name is required.');
      return;
    }

    if (!form.email.trim()) {
      toast.error('Email is required.');
      return;
    }

    setSavingProfile(true);

    try {
      await profileApi.updateProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phoneNumber:
          form.phoneNumber.trim() || null,
      });

      setProfile((current: UserProfile | null) =>
        current
            ? {
                ...current,
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim(),
                phoneNumber: form.phoneNumber.trim() || null,
            }
            : current
        );

      toast.success('Profile updated successfully.');
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          'Unable to update your profile.'
        )
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (
    event: FormEvent
  ) => {
    event.preventDefault();

    if (!passwordForm.currentPassword) {
      toast.error('Current password is required.');
      return;
    }

    if (!passwordForm.newPassword) {
      toast.error('New password is required.');
      return;
    }

    if (
      passwordForm.newPassword !==
      passwordForm.confirmPassword
    ) {
      toast.error('New passwords do not match.');
      return;
    }

    if (
      passwordForm.currentPassword ===
      passwordForm.newPassword
    ) {
      toast.error(
        'Your new password must be different from your current password.'
      );
      return;
    }

    setSavingPassword(true);

    try {
      await profileApi.changePassword({
        currentPassword:
          passwordForm.currentPassword,
        newPassword:
          passwordForm.newPassword,
      });

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      toast.success('Password changed successfully.');
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          'Unable to change your password.'
        )
      );
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-strivre-burgundy" />
        <p>Loading your profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Unable to load your profile.
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left animate-fade-in">
      {/* Personal Information */}
      <form
        onSubmit={handleProfileSubmit}
        className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm"
      >
        <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50/30 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CircleUserRound
                size={20}
                className="text-strivre-burgundy"
              />

              <h3 className="text-lg font-bold text-slate-900">
                Personal Information
              </h3>
            </div>

            <p className="mt-1 text-sm text-slate-500">
              Manage your personal account details.
            </p>
          </div>

          <button
            type="submit"
            disabled={savingProfile}
            className="btn-primary btn-md inline-flex items-center gap-2 self-start"
          >
            {savingProfile ? (
              <Loader2
                size={16}
                className="animate-spin"
              />
            ) : (
              <Save size={16} />
            )}

            {savingProfile
              ? 'Saving...'
              : 'Save Changes'}
          </button>
        </div>

        <div className="grid gap-5 p-6 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">
              First Name
            </span>

            <input
              type="text"
              className="input-base w-full"
              value={form.firstName}
              onChange={(event) =>
                setForm({
                  ...form,
                  firstName: event.target.value,
                })
              }
              required
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">
              Last Name
            </span>

            <input
              type="text"
              className="input-base w-full"
              value={form.lastName}
              onChange={(event) =>
                setForm({
                  ...form,
                  lastName: event.target.value,
                })
              }
              required
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">
              Email Address
            </span>

            <input
              type="email"
              className="input-base w-full"
              value={form.email}
              onChange={(event) =>
                setForm({
                  ...form,
                  email: event.target.value,
                })
              }
              required
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">
              Phone Number
            </span>

            <input
              type="tel"
              className="input-base w-full"
              placeholder="Enter your phone number"
              value={form.phoneNumber}
              onChange={(event) =>
                setForm({
                  ...form,
                  phoneNumber: event.target.value,
                })
              }
            />
          </label>
        </div>
      </form>

      {/* Account & Subscription */}
      <section className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50/30 p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck
              size={20}
              className="text-strivre-burgundy"
            />

            <h3 className="text-lg font-bold text-slate-900">
              Account & Subscription
            </h3>
          </div>

          <p className="mt-1 text-sm text-slate-500">
            Review your account status and platform access.
          </p>
        </div>

        <div className="p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ProfileInfo
              label="Role"
              value={
                profile.roles.length
                  ? profile.roles.join(', ')
                  : 'User'
              }
            />

            <ProfileInfo
              label="Account Status"
              value={profile.status}
            />

            <ProfileInfo
              label="Subscription Tier"
              value={profile.tierName ?? 'No Tier'}
            />

            <ProfileInfo
              label="Last Login"
              value={
                profile.lastLoginDate
                  ? new Date(
                      profile.lastLoginDate
                    ).toLocaleString()
                  : 'Not available'
              }
            />
          </div>

          <div className="mt-6">
            <p className="mb-3 text-sm font-semibold text-slate-700">
              Enabled Add-Ons
            </p>

            {profile.addOns.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {profile.addOns.map((addOn: string) => (
                    <span
                        key={addOn}
                        className="rounded-full bg-strivre-lightburgundy px-3 py-1.5 text-xs font-semibold text-strivre-burgundy"
                    >
                        {addOn}
                    </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                No additional add-ons are assigned to your account.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Security */}
      <form
        onSubmit={handlePasswordSubmit}
        className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm"
      >
        <div className="border-b border-slate-100 bg-slate-50/30 p-6">
          <div className="flex items-center gap-2">
            <KeyRound
              size={20}
              className="text-strivre-burgundy"
            />

            <h3 className="text-lg font-bold text-slate-900">
              Security
            </h3>
          </div>

          <p className="mt-1 text-sm text-slate-500">
            Change your account password.
          </p>
        </div>

        <div className="space-y-5 p-6">
          <label className="block max-w-xl">
            <span className="mb-1 block text-sm font-semibold text-slate-700">
              Current Password
            </span>

            <input
              type="password"
              className="input-base w-full"
              value={passwordForm.currentPassword}
              onChange={(event) =>
                setPasswordForm({
                  ...passwordForm,
                  currentPassword:
                    event.target.value,
                })
              }
              required
            />
          </label>

          <div className="grid max-w-4xl gap-5 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-slate-700">
                New Password
              </span>

              <input
                type="password"
                className="input-base w-full"
                value={passwordForm.newPassword}
                onChange={(event) =>
                  setPasswordForm({
                    ...passwordForm,
                    newPassword:
                      event.target.value,
                  })
                }
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-slate-700">
                Confirm New Password
              </span>

              <input
                type="password"
                className="input-base w-full"
                value={passwordForm.confirmPassword}
                onChange={(event) =>
                  setPasswordForm({
                    ...passwordForm,
                    confirmPassword:
                      event.target.value,
                  })
                }
                required
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={savingPassword}
            className="btn-primary btn-md inline-flex items-center gap-2"
          >
            {savingPassword ? (
              <Loader2
                size={16}
                className="animate-spin"
              />
            ) : (
              <KeyRound size={16} />
            )}

            {savingPassword
              ? 'Changing Password...'
              : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ProfileInfo({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>

      <p className="mt-1 font-semibold text-slate-800">
        {value}
      </p>
    </div>
  );
}